import { Component, input } from '@angular/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromAssistantIcon from './assistants.icon.helper';

/**
 * One skill glyph, from whichever icon set its stored `logo` belongs to.
 *
 * ## Why there is no @switch here any more
 *
 * SB-3519 T9 (#1260) added a fourth icon set, and a fourth `@case` on every one
 * of the five templates that dispatched on `getLogoType()` would have been the
 * fourth chance to forget one — a newly-picked icon would then fall through to
 * `<img>`, which resolves against the SPA and returns `index.html`: a
 * broken-image placeholder plus a junk fetch. `getAssistantIconDescriptor` was
 * already the single dispatcher for the three admin surfaces, so the templates
 * route through it instead and gain new sets for free.
 *
 * ⚠️ **The two-branch shape is load-bearing.** The descriptor returns `null` for
 * BOTH "not a sprite name" and "a URL logo", so the branches must read
 * `null` + a non-empty logo → `<img>`, `null` + empty → nothing at all. Do not
 * collapse them, and do not make the descriptor return a fallback: the three
 * admin surfaces depend on that `null`, and one of them for security reasons
 * (see the descriptor's docblock).
 *
 * ⚠️ **This collapse also fixes a gap on four other surfaces.** Only this
 * component ever carried an `ai` case; the skill card, the desktop conversation
 * header and the mobile top bar rendered the `stars` glyph as a broken `<img>`.
 * Routing them through the descriptor draws the sprite. That is intended, not a
 * regression, and QA should expect it.
 *
 * ## The glyph color belongs to the host, and there is one right answer
 *
 * A skill icon is NOT color-configurable — unlike a **teammate** icon, whose
 * color an admin picks in the visual picker. So the same skill must look the
 * same on every surface, and it did not: six chat surfaces drew it with
 * `text-primary-500/50` while all 17 admin sites, every navigation list and the
 * icon panel used `text-primary-300`.
 *
 * `primary-300` is the right one. It is Figma's only faded-icon token —
 * `Icon/icon-primary-faded` `#87a5fc`, confirmed by `get_variable_defs` on the
 * Skill Builder frame `25459:65782`, which carries no half-alpha primary at all.
 * And `/50` is an ALPHA, so it composites against whatever sits behind it: the
 * same glyph rendered `#9699ff` on white and `#858dfe` on a tinted tile. A token
 * does not move.
 *
 * All six moved, but `introduction.component.ts` needed a second decision and
 * they were measured apart. Its glyph sits on `bg-primary-100`, where the token
 * gives **1.92:1** against 2.34:1 before — the largest drop of the six. Swapping
 * that tile to `bg-primary-50` would have matched the other two tinted tiles and
 * recovered contrast, and it was tried in the browser: the tile all but vanishes
 * into the page at 56/64px. The stronger tint is what gives the largest rendering
 * of the icon its presence, and production ships it that way. So the hue was
 * fixed and the tile left alone, with 1.92:1 accepted knowingly — the skill name
 * sits beside it, so the glyph is not the sole carrier of meaning.
 *
 * Every other surface sits on white (2.38:1, what the admin already ships) or on
 * `bg-primary-50` (2.24:1, what `chat-assistant-profile.component.ts` already
 * shipped). No surface reaches a level not already in production.
 */
@Component({
  selector: 'squadbox-assistant-logo',
  standalone: true,
  imports: [fromSeriousSystem.UseIconDirective],
  template: `
    @let descriptor = fromAssistantIcon.getAssistantIconDescriptor(logo());
    @if (descriptor) {
      <svg [sdUseIcon]="descriptor"></svg>
    } @else if (logo()) {
      <img [src]="logo()" alt="Assistant Logo" />
    }
  `,
})
export class AssistantLogoComponent {
  public readonly logo = input.required<string>();
  protected readonly fromAssistantIcon = fromAssistantIcon;
}
